"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PaymentClient = void 0;
const commerce_core_1 = require("@broadleaf/commerce-core");
const types_1 = require("./types");
class PaymentClient extends commerce_core_1.AbstractClient {
    constructor(options) {
        super({ ...options, basePath: options.basePath || '/api/payment' });
    }
    /**
     * Sends a request to retrieve all of the payments for the given owner.
     *
     * @param ownerId ID of the owner of the payments
     * @param ownerType Type of the owner such as CART or SUBSCRIPTION.
     * @param options Additional request config details such as params and headers
     * @param owningUserType Type of the owning user such as CUSTOMER or ACCOUNT.
     *
     * @returns All of the payments for the given owner.
     */
    async fetchAllPaymentsForOwner(ownerId, ownerType, options, owningUserType) {
        if (!ownerType) {
            ownerType = types_1.DefaultPaymentOwnerType.BLC_CART;
        }
        if (!owningUserType) {
            owningUserType = types_1.DefaultPaymentOwningUserType.BLC_CUSTOMER;
        }
        const uri = this.getPaymentUriByOwningUserType(owningUserType);
        const config = {
            method: 'get',
            url: `${uri}`,
            params: {
                ownerId,
                ownerType
            }
        };
        this.attachNumberedPageableParamsIfPresent(config, options);
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Sends a request to create a new payment with the given details
     *
     * @param createPaymentRequest Details used to create a new payment
     * @param options Additional request config details such as params and headers
     *
     * @returns The new payment.
     */
    async createPayment(createPaymentRequest, options) {
        let owningUserType;
        if (!createPaymentRequest.owningUserType) {
            owningUserType = types_1.DefaultPaymentOwningUserType.BLC_CUSTOMER;
        }
        else {
            owningUserType = createPaymentRequest.owningUserType;
        }
        const uri = this.getPaymentUriByOwningUserType(owningUserType);
        const config = {
            method: 'post',
            url: `${uri}`,
            data: createPaymentRequest
        };
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Sends a request to update the specified payment with the given details.
     *
     * @param paymentId ID of the payment to update
     * @param updatePaymentRequest Request details for updating the payment
     * @param options Additional request config details such as params and headers
     *
     * @returns The updated payment.
     */
    async updatePayment(paymentId, updatePaymentRequest, options) {
        let owningUserType;
        if (!updatePaymentRequest.owningUserType) {
            owningUserType = types_1.DefaultPaymentOwningUserType.BLC_CUSTOMER;
        }
        else {
            owningUserType = updatePaymentRequest.owningUserType;
        }
        const uri = this.getPaymentUriByOwningUserType(owningUserType);
        const config = {
            method: 'patch',
            url: `${uri}/${paymentId}`,
            data: updatePaymentRequest
        };
        this.attachPaymentVersionHeaderIfPresent(config, options);
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Sends a request to archive the specified payment.
     *
     * @param paymentId ID of the payment to archive
     * @param options Additional request config details such as params and headers
     *
     */
    async archivePayment(paymentId, options) {
        const config = {
            method: 'delete',
            url: `/customer-payments/${paymentId}`
        };
        this.attachPaymentVersionHeaderIfPresent(config, options);
        await this.call(config, options);
    }
    /**
     * Records the transaction results for the payment.
     *
     * @param paymentId the id of the payment
     * @param transactionResults the transaction results we want to record on the payment
     * @param options the options to the HTTP call
     * @return a single payment
     */
    async recordTransactionResultsForPayment(paymentId, transactionResults, options) {
        const config = {
            method: 'post',
            url: `/payments/${paymentId}/record-transaction-results`,
            data: transactionResults
        };
        this.attachPaymentVersionHeaderIfPresent(config, options);
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Sends the request to the payment gateway to get the transaction result using, records
     * the result to the appropriate transaction and payment.
     * <p>
     * This can be used, for example, to verify that a customer has approved a payment for
     * transactions that require 3D security checks.
     *
     * @param gatewayType the gateway type
     * @param request the parameters for request
     * @param options the options to the HTTP call
     * @return a transaction result
     */
    async handleTransactionResult(gatewayType, request, options) {
        const config = {
            method: 'post',
            url: `/handle-3ds-transaction-result/${gatewayType}`,
            data: request
        };
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Sends the request to the payment gateway to record the result to
     * the appropriate transaction and payment.
     * <p>
     * This can be used, for example, to verify that a customer has
     * approved a payment for transactions that require to add bank details.
     *
     * @param gatewayType the gateway type
     * @param request the parameters for request
     * @param options the options to the HTTP call
     * @return a transaction result
     */
    async handleExternalTransactionResult(gatewayType, request, options) {
        const config = {
            method: 'post',
            url: `/handle-external-transaction-result/${gatewayType}`,
            data: request
        };
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Attaches the payment version header to the request `config` if present on `options`.
     *
     * @param config The axios request config to modify
     * @param options Options specifying additional parameters to be used to modify the `config`
     */
    attachPaymentVersionHeaderIfPresent(config, options) {
        if (options?.version !== undefined) {
            if (!config.headers) {
                config.headers = {};
            }
            config.headers['X-Payment-Version'] = String(options.version);
        }
    }
    /**
     * Attaches numbered pageable params (page, size, etc.) to the request `config` if present on `options`.
     *
     * @param config The axios request config to modify
     * @param options Options specifying additional parameters to be used to modify the `config`
     */
    attachNumberedPageableParamsIfPresent(config, options) {
        if (options?.page) {
            config.params.offset = options.page;
        }
        if (options?.size) {
            config.params.size = options.size;
        }
    }
    /**
     * Gets the payment uri for the given payment owning user type
     *
     * @param owningUserType the payment uri for the given payment owning user type
     */
    getPaymentUriByOwningUserType(owningUserType) {
        if (owningUserType === types_1.DefaultPaymentOwningUserType.BLC_ACCOUNT) {
            return '/account-payments';
        }
        return '/customer-payments';
    }
}
exports.PaymentClient = PaymentClient;
//# sourceMappingURL=payment-client.js.map